import React, { useContext, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useHistoryLocation } from 'app/components/router/History';

export interface ISharedAnchorState {
  anchorChangeCounter: number;
  anchor: string;
}

export interface ISharedAnchorControl {
  scrollToAnchor(anchor?: string): void;
}

const SharedAnchorControlContext = React.createContext<ISharedAnchorControl | undefined>(undefined);
const SharedAnchorContext = React.createContext<ISharedAnchorState | undefined>(undefined);

export const GetSharedAnchorControl = SharedAnchorControlContext.Consumer;
export const GetAnchorScrollingState = SharedAnchorContext.Consumer;

export const useAnchorScrolling = () => useContext(SharedAnchorControlContext);
export const useAnchorScrollingState = () => useContext(SharedAnchorContext);

/**
 * This context is used to manage anchor scrolling behavior. It's particularly useful
 * when coordinating behavior between multiple panes, such as in the editor.
 */
const AnchorScrollingContextProvider: React.FC = ({ children }) => {
  const location = useHistoryLocation();

  // when this counter is increased, Consumers know to fire off anchor scrolling
  const [anchorChangeCounter, setAnchorChangeCounter] = useState(0);
  const [anchor, setAnchor] = useState(location.hash ? location.hash.substr(1) : '');

  const previousHash = useRef<string | null>(null);

  const scrollToAnchor = useCallback((targetAnchor?: string) => {
    // Consumers inside ScrollPositions down the tree will respond to changes in the
    // anchorChangeCounter and scroll as needed.
    setAnchor(targetAnchor || location.hash);
    setAnchorChangeCounter(anchorChangeCounter + 1);
  }, [location.hash, anchorChangeCounter]);

  useEffect(() => {
    if (previousHash.current !== null && previousHash.current !== location.hash) {
      scrollToAnchor(location.hash.substr(1));
    }
    previousHash.current = location.hash;
  }, [location.hash, scrollToAnchor]);

  const sharedAnchorCtrl = useMemo(() => ({
    scrollToAnchor,
  }), [scrollToAnchor]);

  const sharedAnchorCtx = useMemo(() => ({
    anchor,
    anchorChangeCounter,
  }), [anchor, anchorChangeCounter]);

  return (
    <SharedAnchorControlContext.Provider value={sharedAnchorCtrl}>
      <SharedAnchorContext.Provider value={sharedAnchorCtx}>
        {children}
      </SharedAnchorContext.Provider>
    </SharedAnchorControlContext.Provider>
  );
};

export default AnchorScrollingContextProvider;
